














import Vue from "vue";
import { getCountryByIp } from '@/utilities/allCountriesCodes';
import { validatePhone } from "@/utilities/phoneNumber";
import intlTelInput from 'intl-tel-input/build/js/intlTelInputWithUtils.min.js';
import storage from '@/libs/SafeStorage';

export default Vue.extend({
    name: 'TactfulTelInput',
    props: {
        /**
       * Reflects the v-model value
       */
        value: {
            type: String,
            required: true,
            default: () => {
                return '';
            },
        },
        /**
       * To disable selection
       */
        disabled: {
            type: Boolean,
            required: false,
            default: () => {
                return false;
            },
        },
        /**
       * To add placeholder
       */
        placeholder: {
            type: String,
            required: false,
            default: () => {
                return '';
            },
        },
        /**
       * To add validation rules
       */
        rules: {
            type: [String, Object],
            required: false,
            default: () => {
                return '';
            },
        },
        /**
       * To prevent choose a default country 
       */
        preventDefaultCountry: {
            type: Boolean,
            required: false,
            default: () => {
                return false;
            },
        },
        validateWithCountry: {
            type: Boolean,
            required: false,
            default: () => {
                return false;
            }
        },
        inputId: {
            type: String,
            required: false,
            default: () => {
                return "";
            }
        }
    },
    data() {
        return {
            selectedCountry: null,
            validPhoneNumber: true,
            intlTelInput: null,
            nationalNumber: "",
        };
    },
    computed: {
        inputVal: {
            get() {
                if (this.testCharacters(this.value))
                    return this.value;
                else return 0;
            },
            set(val: any) {
                if (this.testCharacters(val))
                    this.$emit('input', val);
            }
        },
        isRequired() {
            if (typeof this.rules == 'string') return this.rules.includes("required");
            return !!(this.rules?.required);
        },
    },
    watch: {
        value: {
            handler(val, oldVal) {
                if (val === oldVal || !this.intlTelInput) return;
                if (this.getFormattedPhone() !== val) this.setPhoneNumber(val);
            },
        },
        disabled: {
            handler(val) {
                this.setDiableIntlTelInputState(val);
            }
        }
    },
    mounted() {
        this.initializeTelInputPlugin();
    },
    beforeDestroy() {
        // Remove the plugin from the input, and unbind any event listeners
        if (this.intlTelInput) this.intlTelInput.destroy();
    },

    methods: {
        setDiableIntlTelInputState(disabled) {
            if (!this.intlTelInput) return;
            const telInputInstance = intlTelInput.getInstance(this.$refs['tactful-tel-input']);
            const selectedCountryContainer = telInputInstance.selectedCountry;

            this.intlTelInput.disabled = disabled;
            if (disabled) {
                selectedCountryContainer?.setAttribute("disabled", "true");
            } else {
                selectedCountryContainer?.removeAttribute("disabled");
            }
        },
        initializeTelInputPlugin() {
            this.readingCountryInProgress = true;
            const telInputOptions = {
                geoIpLookup: (success, failure) => {
                    this.getCurrentCountry()
                        .then(function (countryCode) { return success(countryCode); })
                        .catch(function () { failure(); });
                },
                separateDialCode: true,
                formatAsYouType: false,
                containerClass: 'tc--b-tel-input',
                disabled: this.disabled,
                useFullscreenPopup: false,
            };
            if (!this.value && !this.preventDefaultCountry) telInputOptions['initialCountry'] = "auto";
            this.intlTelInput = intlTelInput(this.$refs['tactful-tel-input'], telInputOptions);
            this.intlTelInput.promise.then(() => {
                this.setPhoneNumber(this.value);
                return this.readingCountryInProgress = false;
            }).catch(() => this.readingCountryInProgress = false);
        },
        updateAndValidate() {
            const phoneNumber = this.getFormattedPhone();
            this.updateNationalNumber(phoneNumber);
            this.inputVal = phoneNumber;
            this.updateCountryCode();
            this.validatePhoneNumber();
        },
        updateCountryCode() {
            const newSelectedCountry = this.intlTelInput?.getSelectedCountryData() || {};
            if (this.selectedCountry && this.selectedCountry.iso2 === newSelectedCountry.iso2) return;
            this.selectedCountry = newSelectedCountry;
            this.$emit('countryCode', newSelectedCountry);
        },
        blurEventHandler() {
            this.$emit("blur", this.inputVal);
        },
        testCharacters(phone: string) {
            return /^[()\-+0-9\s]*$/.test(phone);
        },
        //prevent any character in input except numbers and + only at the beginning
        inputHandler(e: any) {
            const charCode = (e.which) ? e.which : e.keyCode;
            const isValidChar = (charCode >= 48 && charCode <= 57) || (e.target.selectionStart == 0 && charCode == 43);
            if (!isValidChar) e.preventDefault();
            return isValidChar;
        },
        //get the current country by IP address
        async getCurrentCountry() {
            if (this.preventDefaultCountry) throw new Error('Default Country is prevented');
            let countryCode = undefined;
            if (!this.checkIfUserCountryInLocalStorage()) {
                try {
                    countryCode = await getCountryByIp();
                    this.addCountryLocationInLocalstorage(countryCode);
                } catch (error) {
                    console.error(error);
                    countryCode = "EG";
                }
            } else {
                countryCode = storage.get("user-country");
            }
            if (!countryCode) throw new Error('Failed to get country code');
            return countryCode;
        },
        addCountryLocationInLocalstorage(countryIso2: string) {
            if (!storage.get("user-country")) {
                storage.set("user-country", countryIso2);
            }
        },
        checkIfUserCountryInLocalStorage() {
            return storage.get("user-country");
        },
        getFormattedPhone(phonenumber?: string, format: number = intlTelInput.utils.numberFormat.E164) {
            if (!this.intlTelInput) return "";
            const phoneNumber = phonenumber || this.intlTelInput.getNumber();
            const selectedCountry = this.intlTelInput.getSelectedCountryData()?.iso2;
            return intlTelInput.utils.formatNumber(phoneNumber, selectedCountry, format).replace(/\s/g, "");
        },
        getPhoneWithoutCountryCode() {
            const phoneNumber = this.getFormattedPhone();
            const countryCode = this.intlTelInput?.getSelectedCountryData()?.dialCode || "";
            return phoneNumber.replace(new RegExp(`^\\+?${countryCode}`), "");
        },
        validatePhoneNumber() {
            if (this.validateWithCountry) {
                const phoneNumber = this.getFormattedPhone();
                // if phoneNumber is empty string, the plugin will mark it as invalid because it's TOO_SHORT
                this.validPhoneNumber = phoneNumber ? this.intlTelInput?.isValidNumberPrecise() : true;
            } else {
                const phoneNumber = this.getPhoneWithoutCountryCode();
                this.validPhoneNumber = validatePhone(phoneNumber);
            }
            this.$emit("validatePhone", this.validPhoneNumber);
        },
        updateNationalNumber(phoneNumber) {
            try {
                this.nationalNumber = this.getFormattedPhone(phoneNumber, intlTelInput.utils.numberFormat.E164);
            } catch {
                this.nationalNumber = '';
            }
        },
        setPhoneNumber(phoneNumber) {
            this.intlTelInput?.setNumber(phoneNumber);
            this.updateNationalNumber(phoneNumber);
        }
    }
});
